import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";

const POSITIONS = [
  "Line Cook",
  "Prep Cook",
  "Server",
  "Bartender",
  "Host / Hostess",
  "Busser",
  "Dishwasher",
  "Lounge / Security",
  "Manager",
  "Other",
];

export default function Careers() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    experience_years: "",
    availability: "",
    resume: "",
    cover_message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.position) {
      toast.error("Please select a position.");
      return;
    }
    if (form.resume.trim().length < 30) {
      toast.error("Paste a bit more of your resume / experience.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/job-applications", {
        ...form,
        experience_years: form.experience_years
          ? parseInt(form.experience_years, 10)
          : null,
      });
      toast.success("Application received — we'll be in touch soon.");
      setForm({
        name: "",
        email: "",
        phone: "",
        position: "",
        experience_years: "",
        availability: "",
        resume: "",
        cover_message: "",
      });
    } catch (err) {
      toast.error("Couldn't send your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      id="careers"
      data-testid="careers-section"
      className="trio-section py-24 lg:py-32 bg-[var(--trio-surface)] border-b border-[var(--trio-border)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-4">
              Join the Crew
            </div>
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.9] mb-6">
              Come<br /><span className="text-[var(--trio-primary)]">Work With Us.</span>
            </h2>
            <p className="text-base lg:text-lg text-[var(--trio-muted)] leading-relaxed max-w-md">
              Cooks, servers, bartenders, hosts and lounge crew. If you take
              pride in your work and like a busy night, we want to meet you.
            </p>

            <div className="mt-10 space-y-4 max-w-md">
              {[
                ["Flexible Schedules", "Day, night, weekend shifts."],
                ["Room to Grow", "Cross-train across kitchen, bar, and floor."],
              ].map(([t, d]) => (
                <div key={t} className="flex gap-4">
                  <div className="w-10 h-px bg-[var(--trio-primary)] mt-3" />
                  <div>
                    <div className="font-display uppercase text-lg tracking-tight">{t}</div>
                    <div className="text-sm text-[var(--trio-muted)]">{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="career-form"
            className="lg:col-span-7 bg-[var(--trio-bg)] border border-[var(--trio-border)] p-8 lg:p-10 space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="c-name" className="text-xs tracking-widest uppercase">
                  Full Name
                </Label>
                <Input
                  id="c-name"
                  required
                  data-testid="career-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
                />
              </div>
              <div>
                <Label htmlFor="c-email" className="text-xs tracking-widest uppercase">
                  Email
                </Label>
                <Input
                  id="c-email"
                  type="email"
                  required
                  data-testid="career-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
                />
              </div>
              <div>
                <Label htmlFor="c-phone" className="text-xs tracking-widest uppercase">
                  Phone
                </Label>
                <Input
                  id="c-phone"
                  required
                  data-testid="career-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
                />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase">Position</Label>
                <Select
                  value={form.position}
                  onValueChange={(v) => update("position", v)}
                >
                  <SelectTrigger
                    data-testid="career-position"
                    className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)]"
                  >
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none">
                    {POSITIONS.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="c-years" className="text-xs tracking-widest uppercase">
                  Years Experience
                </Label>
                <Input
                  id="c-years"
                  type="number"
                  min="0"
                  data-testid="career-years"
                  value={form.experience_years}
                  onChange={(e) => update("experience_years", e.target.value)}
                  className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
                />
              </div>
              <div>
                <Label htmlFor="c-avail" className="text-xs tracking-widest uppercase">
                  Availability
                </Label>
                <Input
                  id="c-avail"
                  data-testid="career-availability"
                  placeholder="Nights, weekends, full-time..."
                  value={form.availability}
                  onChange={(e) => update("availability", e.target.value)}
                  className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="c-resume" className="text-xs tracking-widest uppercase">
                Paste your resume / past jobs
              </Label>
              <Textarea
                id="c-resume"
                required
                data-testid="career-resume"
                rows={6}
                placeholder="Tell us where you've worked, what you cooked, served or poured. Paste a resume if you have one."
                value={form.resume}
                onChange={(e) => update("resume", e.target.value)}
                className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
              />
            </div>
            <div>
              <Label htmlFor="c-cover" className="text-xs tracking-widest uppercase">
                Why Trio?
              </Label>
              <Textarea
                id="c-cover"
                data-testid="career-cover"
                rows={3}
                value={form.cover_message}
                onChange={(e) => update("cover_message", e.target.value)}
                className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="career-submit"
              className="w-full sm:w-auto bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-8 py-4 text-sm tracking-widest uppercase disabled:opacity-60 transition-colors"
            >
              {submitting ? "Sending..." : "Submit Application"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
